import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BadgeCheck, BookOpenCheck, Mail, RefreshCw } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { ObangsaekStripe } from '@/components/editorial';

const PRINCIPLES = [
  {
    icon: BookOpenCheck,
    title: 'Start with primary sources',
    titleKo: '1차 출처 우선',
    text: 'Government agencies, regulators, and providers come before summaries, forums, or search snippets.',
    textKo: '정부 기관, 규제 기관, 서비스 제공자의 원문을 먼저 확인합니다.',
  },
  {
    icon: RefreshCw,
    title: 'Show when facts were checked',
    titleKo: '검증 날짜 공개',
    text: 'Rules and fees change. Every flagship guide publishes its last verification date and maintenance notes.',
    textKo: '규정과 수수료는 바뀌므로 주요 가이드마다 마지막 검증 날짜와 관리 방식을 공개합니다.',
  },
  {
    icon: BadgeCheck,
    title: 'Be clear about limits',
    titleKo: '한계를 명확하게',
    text: 'We distinguish general information from legal, tax, or financial advice and label estimates as estimates.',
    textKo: '일반 정보와 법률·세무·재무 자문을 구분하고 추정치는 추정치로 표시합니다.',
  },
] as const;

export default function About() {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="About KoreanHub — Our Editorial Approach"
        description="Why KoreanHub publishes sourced, maintained guides for people navigating life between the United States and Korea."
        path="/about"
      />

      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <p className="text-sm font-semibold text-jeok-500">KoreanHub 소개</p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
            {isKorean
              ? '미국과 한국 사이의 복잡한 문제를 더 명확하게.'
              : 'Making the complicated parts of life between the US and Korea clearer.'}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-7 text-ink-700 dark:text-ink-200">
            {isKorean
              ? 'KoreanHub는 송금, 비자, 한국 정착처럼 시간과 비용이 많이 드는 질문에 답하는 독립적인 편집 프로젝트입니다. 아직 모든 것을 다루지는 않습니다. 대신 공개한 가이드를 출처와 검증 날짜와 함께 책임 있게 관리합니다.'
              : 'KoreanHub is an independent editorial project answering the questions that cost people time and money: remittance, visas, and settling in Korea. We do not cover everything. We maintain what we do publish, with sources and visible verification dates.'}
          </p>
        </div>
      </header>

      <ObangsaekStripe />

      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <section>
          <h2 className="text-2xl font-bold text-ink-900 dark:text-ink-50">
            {isKorean ? '편집 원칙' : 'How we work'}
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {PRINCIPLES.map(({ icon: Icon, title, titleKo, text, textKo }) => (
              <article key={title} className="rounded-xl border border-ink-200 bg-white p-5 shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-cheong-500/10 text-cheong-500 dark:text-cheong-dark">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-semibold text-ink-900 dark:text-ink-50">
                  {isKorean ? titleKo : title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-ink-600 dark:text-ink-300">
                  {isKorean ? textKo : text}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 overflow-hidden rounded-2xl border border-ink-200 md:grid-cols-2 dark:border-surface-dark-border">
          <div className="bg-ink-50 p-6 sm:p-8 dark:bg-white/[0.03]">
            <h2 className="text-xl font-bold text-ink-900 dark:text-ink-50">
              {isKorean ? '현재의 초점' : 'What we are focused on now'}
            </h2>
            <p className="mt-3 text-sm leading-7 text-ink-700 dark:text-ink-200">
              {isKorean
                ? '현재는 미국과 한국을 오가는 독자에게 가장 유용한 세 가지 영역, 즉 송금과 국경 간 금융, F-4 비자, 한국 정착 절차에 집중합니다.'
                : 'Our current focus is intentionally narrow: US–Korea money questions, the F-4 visa, and the practical sequence of settling in Korea.'}
            </p>
          </div>
          <div className="bg-white p-6 sm:p-8 dark:bg-surface-dark-card">
            <h2 className="text-xl font-bold text-ink-900 dark:text-ink-50">
              {isKorean ? '잘못된 정보를 발견하셨나요?' : 'Found something wrong or incomplete?'}
            </h2>
            <p className="mt-3 text-sm leading-7 text-ink-700 dark:text-ink-200">
              {isKorean
                ? '영사관, 은행 지점, 관공서의 실제 경험은 가이드를 개선하는 데 도움이 됩니다. 출처 링크와 함께 알려주세요.'
                : 'Real experience at a consulate, bank branch, or government office can improve a guide. Send us the detail and, when possible, the source.'}
            </p>
            <Link to="/contact" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-jeok-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#A00D25]">
              <Mail className="h-4 w-4" aria-hidden="true" />
              {isKorean ? '수정 제보하기' : 'Submit a correction'}
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
